import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSuperuser } from "@/hooks/useSuperuser";

const ALLOWED_ADMIN_NAME = "sanna dahlin";
const ALLOWED_ADMIN_USER_IDS = new Set([
  "c4c7ce2e-07ec-4312-b5c7-6245dae288ba",
]);
const ALLOWED_ADMIN_EMAILS = new Set([
  "sanna.helena.dahlin@gmail.com",
]);

const normalize = (value?: string | null): string => {
  if (!value) return "";
  return value.toLowerCase().replace(/\s+/g, " ").trim();
};

const isAllowedAdminUser = (
  session: { user?: { id?: string; email?: string | null; user_metadata?: Record<string, unknown> } } | null,
) => {
  const user = session?.user;
  if (!user) return false;

  const metadata = user.user_metadata || {};
  const fullName = normalize(
    (metadata.full_name as string | undefined) ||
      (metadata.name as string | undefined) ||
      (metadata.fullName as string | undefined),
  );
  const email = normalize(user.email || "");
  const userId = user.id || "";

  return (
    (userId && ALLOWED_ADMIN_USER_IDS.has(userId)) ||
    (email && ALLOWED_ADMIN_EMAILS.has(email)) ||
    fullName === ALLOWED_ADMIN_NAME
  );
};

export function useAdminAccess() {
  const { isSuperuser, loading: superuserLoading } = useSuperuser();
  const [isAllowedUser, setIsAllowedUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (isMounted) {
        setIsAllowedUser(isAllowedAdminUser(data.session));
        setLoading(false);
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkUser();
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const isAdmin = useMemo(() => {
    return isSuperuser && isAllowedUser;
  }, [isSuperuser, isAllowedUser]);

  return { isAdmin, loading: loading || superuserLoading };
}
